/**
 * Simple counting semaphore for bounding concurrent async operations.
 * Used to prevent overwhelming external APIs (e.g., FactSet TTF) when
 * multiple tool calls fire in parallel.
 */
export class Semaphore {
  private queue: Array<{
    resolve: () => void;
    reject: (error: Error) => void;
    signal?: AbortSignal;
    onAbort?: () => void;
    settled: boolean;
  }> = [];
  private active = 0;

  /** @param limit Maximum number of concurrent operations allowed. */
  constructor(private readonly limit: number) {
    if (!Number.isInteger(limit) || limit < 1) {
      throw new RangeError("Semaphore limit must be a positive integer");
    }
  }

  /** Wait until a slot is available, then reserve it. */
  async acquire(signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) throw semaphoreAbortError();
    if (this.active < this.limit) {
      this.active++;
      return;
    }
    return new Promise<void>((resolve, reject) => {
      const waiter: (typeof this.queue)[number] = {
        resolve,
        reject,
        signal,
        settled: false,
      };
      const onAbort = (): void => {
        if (waiter.settled) return;
        waiter.settled = true;
        const index = this.queue.indexOf(waiter);
        if (index >= 0) this.queue.splice(index, 1);
        signal?.removeEventListener("abort", onAbort);
        reject(semaphoreAbortError());
      };
      waiter.onAbort = onAbort;
      this.queue.push(waiter);
      signal?.addEventListener("abort", onAbort, { once: true });
      if (signal?.aborted) onAbort();
    });
  }

  /** Release a slot, unblocking the next queued caller if any. */
  release(): void {
    this.active--;
    while (this.queue.length > 0) {
      const next = this.queue.shift();
      if (!next) return;
      next.signal?.removeEventListener("abort", next.onAbort!);
      if (next.settled) continue;
      next.settled = true;
      if (next.signal?.aborted) {
        next.reject(semaphoreAbortError());
        continue;
      }
      this.active++;
      next.resolve();
      return;
    }
  }

  /** Run an async function with the semaphore held. Releases on completion or error. */
  async run<T>(fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
    await this.acquire(signal);
    try {
      if (signal?.aborted) throw semaphoreAbortError();
      return await fn();
    } finally {
      this.release();
    }
  }
}

function semaphoreAbortError(): Error {
  const error = new Error("Semaphore wait was aborted");
  error.name = "AbortError";
  return error;
}
